import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { StatusTone } from 'utils/fleet-dashboard-theme';

/**
 * A single number with the label that names it.
 *
 * A plain figure wears no colour: nothing about a robot count is good or bad, so the number is
 * the only thing on the card. A status count is given a `tone` instead, and then the whole card
 * carries it - a tinted surface, a bar down the leading edge in the status colour, and the figure
 * in the status ink - so the four buckets are told apart before a single label is read while every
 * value still clears the surface behind it.
 *
 * Given an `onClick` the card becomes a toggle; it stays white until `selected`, so a row of
 * choices shows which one is active.
 */
const StatusCardWidget: React.FC<{
  /** The status the number belongs to. Left out for a figure that means nothing on its own. */
  tone?: StatusTone;
  value: number | string;
  percent?: number;
  label: string;
  /** What exactly is counted, offered on the label for the numbers that need a definition. */
  info?: string;
  selected?: boolean;
  onClick?: () => void;
}> = ({ tone, value, percent, label, info, selected = false, onClick }) => {
  const isToggle = onClick != null;
  const isTinted = tone != null && (!isToggle || selected);

  const labelText = (
    <Typography
      variant="body2"
      fontWeight={tone ? 600 : 400}
      sx={{
        color: tone ? tone.ink : 'text.secondary',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );

  const keyDownHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <Card
      variant="outlined"
      role={isToggle ? 'button' : 'group'}
      aria-label={label}
      aria-pressed={isToggle ? selected : undefined}
      tabIndex={isToggle ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isToggle ? keyDownHandler : undefined}
      sx={{
        borderRadius: 2,
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        pl: tone ? 2.5 : 2,
        pr: 2,
        py: 2,
        borderColor: isToggle && !selected ? undefined : tone?.mark,
        borderWidth: selected ? 2 : undefined,
        backgroundColor: isTinted ? tone?.tint : undefined,
        cursor: isToggle ? 'pointer' : undefined,
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
        '&:hover': isToggle && !selected ? { backgroundColor: 'action.hover' } : undefined,
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {tone && (
        <Box
          sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 6, bgcolor: tone.mark }}
        />
      )}
      <Stack gap={1.5} height="100%" justifyContent="space-between">
        {info ? (
          <Tooltip title={info}>
            <Box minWidth={0} sx={{ cursor: 'help' }}>
              {labelText}
            </Box>
          </Tooltip>
        ) : (
          labelText
        )}
        {/* The value and its share stay one text node, so the whole figure reads as one thing. */}
        <Typography variant="h4" sx={{ color: tone ? tone.ink : 'text.primary' }}>
          {percent != null ? `${value} (${percent}%)` : value}
        </Typography>
      </Stack>
    </Card>
  );
};

export default React.memo(StatusCardWidget);
